import type { JsonValue } from '../online-eval.types';

// Strings this long start collapsed, so a transcript doesn't bury everything after it
const COLLAPSED_STRING_CHARS = 2000;
// Single-line strings up to this long read best on the same line as their key
const INLINE_STRING_CHARS = 200;

const isContainer = (
  value: JsonValue
): value is JsonValue[] | { [key: string]: JsonValue } =>
  value !== null && typeof value === 'object';

const isInline = (value: JsonValue) =>
  typeof value === 'string'
    ? value.length <= INLINE_STRING_CHARS && !value.includes('\n')
    : !isContainer(value);

// Shows JSON with every string as the text it holds, newlines and tabs included, rather
// than escaped. Short values sit next to their key; nested values and long strings get a
// collapsible section, set off by a faint rule rather than a box.
export const JsonView = ({ value }: { value: JsonValue }) => {
  if (typeof value === 'string') {
    return (
      <pre className="whitespace-pre-wrap break-words font-mono text-xs">
        {value}
      </pre>
    );
  }
  if (!isContainer(value)) {
    return <code className="font-mono text-xs">{JSON.stringify(value)}</code>;
  }
  const entries: [string, JsonValue][] = Array.isArray(value)
    ? value.map((item, index) => [`[${index}]`, item])
    : Object.entries(value);
  if (entries.length === 0) {
    return (
      <code className="font-mono text-xs">
        {Array.isArray(value) ? '[]' : '{}'}
      </code>
    );
  }
  return (
    <div className="space-y-1">
      {entries.map(([key, item]) =>
        isInline(item) ? (
          <div key={key} className="flex gap-2 font-mono text-xs">
            <span className="shrink-0 text-muted-foreground">{key}:</span>
            <span className="whitespace-pre-wrap break-words">
              {typeof item === 'string' ? item : JSON.stringify(item)}
            </span>
          </div>
        ) : (
          <details
            key={key}
            open={
              typeof item !== 'string' || item.length < COLLAPSED_STRING_CHARS
            }
          >
            <summary className="cursor-pointer select-none font-mono text-xs text-muted-foreground hover:text-foreground">
              {key}
              {typeof item === 'string' &&
                ` · ${item.length.toLocaleString()} characters`}
            </summary>
            <div className="ml-1 mt-1 border-l border-border/40 pl-3">
              <JsonView value={item} />
            </div>
          </details>
        )
      )}
    </div>
  );
};
